<script setup>
import { ref, onMounted, watch } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import { useRouter } from "vue-router";

const router = useRouter();
const $loading = useLoading(loaderData);

const taskData = ref({
  title: "",
  description: "",
  type: "manual",
  workflow_id: null,
  workflow_step_id: null,
});

const users = ref([]);
const teams = ref([]);
const userSearchQuery = ref("");
const teamSearchQuery = ref("");
const selectedUser = ref(null);
const selectedTeam = ref(null);
const assignmentType = ref('user'); // 'user' | 'team'

const submitBtn = ref(1);

const workflowSearchQuery = ref('');

const workflows = ref([]);
const workflowSteps = ref({});
watch(workflowSearchQuery, () => {
    fetchWorkflows();
});

const fetchWorkflows = () => {
    axios
        .get(`/all_workflows?search=${workflowSearchQuery.value}`)
        .then((response) => {
            if (response.status == "success") {
                workflows.value = response.data.workflows.data;
                fetchWorkflowSteps();
            }
        });
};

const fetchWorkflowSteps = () => {
    workflows.value.forEach(workflow => {
        axios
            .get(`/workflow_steps?workflow_id=${workflow.id}`)
            .then((response) => {
                if (response.status == "success") {
                    workflowSteps.value[workflow.id] = response.data.workflow_steps;
                }
            });
    });
};

// Get users for assignments
const getUsers = () => {
  axios
    .get(`/get-all-users?search_query=${userSearchQuery.value}`)
    .then(async (response) => {
      if (response.status == "success") {
        users.value = Object.entries(response.data.users).map(
          ([index, value]) => {
            return {
              value: value.id,
              title: `${value.first_name} ${value.last_name}`,
            };
          }
        );
      }
    });
};

// Watch for changes in search query
watch(userSearchQuery, () => {
  getUsers();
});

// Get teams for assignment
const getTeams = () => {
  axios
    .get(`/get-all-teams?search_query=${teamSearchQuery.value}`)
    .then((response) => {
      if (response.status == "success") {
        teams.value = Object.entries(response.data.teams).map(
          ([index, value]) => ({
            value: value.id,
            title: value.name,
          })
        );
      }
    });
};

watch(teamSearchQuery, () => {
  getTeams();
});

// Submit the task creation form
const createTask = async () => {
  if (!taskData.value.title) {
    toast.error("Title is required", { autoClose: 2000 });
    return;
  }

  if (assignmentType.value === 'user' && !selectedUser.value) {
    toast.error("Please select a user to assign", { autoClose: 2000 });
    return;
  }

  if (assignmentType.value === 'team' && !selectedTeam.value) {
    toast.error("Please select a team to assign", { autoClose: 2000 });
    return;
  }

  let loader = $loading.show();
  submitBtn.value = 0;

  // Prepare the data for submission
  const dataToSubmit = {
    title: taskData.value.title,
    description: taskData.value.description,
    type: taskData.value.type,
    assignments: assignmentType.value === 'user' && selectedUser.value
      ? [{ user_id: selectedUser.value, status: 'pending' }]
      : [],
    team_assignment: assignmentType.value === 'team' ? selectedTeam.value : null,
    workflow_id: taskData.value.workflow_id || null,
    workflow_step_id: taskData.value.workflow_step_id || null,
  };

  axios
    .post("/tasks", dataToSubmit)
    .then((response) => {
      if (response.status == "success") {
        toast.success(response.message, {
          autoClose: 2000,
        });
        router.push({ name: "tasks-list" });
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch((error) => {
      console.error(error);
      toast.error("An error occurred while creating the task", {
        autoClose: 2000,
      });
    })
    .finally(() => {
      loader.hide();
      submitBtn.value = 1;
    });
};

// Cancel task creation and return to list
const cancel = () => {
  router.push({ name: "tasks-list" });
};

// Initialize page
onMounted(() => {
  getUsers();
  getTeams();
  fetchWorkflows();
});

definePage({ meta: { requiresAuth: true } });
</script>

<template>
  <div>
    <div class="d-flex justify-end gap-1 mb-4">
      <VBtn variant="tonal" color="light" @click="cancel"> Discard </VBtn>
      <VBtn
        variant="tonal"
        color="primary"
        :loading="submitBtn === 0"
        @click="createTask"
      >
        Create Task
      </VBtn>
    </div>
    <VCard title="Create Task">
      <VCardText>
        <VRow>
          <VCol cols="12">
            <VTextField
              v-model="taskData.title"
              label="Task Title"
              required
              hint="Enter a descriptive title for the task"
            />
          </VCol>
          <VCol cols="12">
            <VTextarea
              v-model="taskData.description"
              label="Description"
              hint="Provide details about what this task involves"
              rows="3"
            />
          </VCol>
          <VCol cols="12" md="6">
              <AppAutocomplete
                  v-model:search="workflowSearchQuery"
                  v-model="taskData.workflow_id"
                  :items="workflows.map(w => ({title: w.id + ' - ' + w.title, value: w.id}))"
                  clearable
                  placeholder="Select workflow (optional)"
              />
          </VCol>
          <VCol cols="12" md="6" v-if="taskData.workflow_id" >
              <AppSelect
                  v-model="taskData.workflow_step_id"
                  :items="(workflowSteps[taskData.workflow_id] || []).map(s => ({title: s.title, value: s.id}))"
                  clearable
                  placeholder="Select workflow step"
              />
          </VCol>
        </VRow>

        <VDivider class="my-4" />

        <div
          class="d-flex flex-wrap justify-start justify-sm-space-between gap-y-4 gap-x-6 mb-6"
        >
          <div class="d-flex flex-column justify-center">
            <h3 class="mb-0">Task Assignment</h3>
            <span class="text-caption">Assign this task to a user or team</span>
          </div>
        </div>

        <VCard class="mb-6 pa-4">
          <VRadioGroup v-model="assignmentType" inline class="mb-4">
            <VRadio label="User" value="user" />
            <VRadio label="Team" value="team" />
          </VRadioGroup>
          <VRow v-if="assignmentType === 'user'">
            <VCol cols="12" md="6">
              <VAutocomplete
                v-model="selectedUser"
                :items="users"
                v-model:search-input="userSearchQuery"
                label="Select User"
                clearable
              />
            </VCol>
          </VRow>
          <VRow v-else>
            <VCol cols="12" md="6">
              <VAutocomplete
                v-model="selectedTeam"
                :items="teams"
                v-model:search-input="teamSearchQuery"
                label="Select Team"
                clearable
              />
            </VCol>
          </VRow>
        </VCard>
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped>
.assignment-container:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
}
</style>
